import {Info} from "lucide-react";
import {Star, Activity, Zap, Users} from "lucide-react";
import Tooltip from "components/Tooltip";
import {FC} from "react";

type Level = "low" | "average" | "high";

interface Props {
  knowledge: Level;
  activity: Level;
  speed: Level;
  single?: boolean;
}

function getOverviewConfig(knowledge: Level, activity: Level, speed: Level) {
  if (activity === "low") return {text: "Inactive", sublabel: "Low activity", color: "#F57A47"};
  if (activity === "average") return {text: "Inconsistent", sublabel: "Activity inconsistent", color: "#F5A623"};
  if (knowledge === "high" && speed === "high")
    return {text: "Exceptional!", sublabel: "Knowledge + Activity + Speed", color: "#57B98F"};
  if (knowledge === "high") return {text: "Top Talent", sublabel: "Knowledge + Activity", color: "#57B98F"};
  if (speed === "high") return {text: "Rising Talent", sublabel: "Activity + Speed", color: "#57B98F"};
  if (speed === "average" && knowledge === "average")
    return {text: "Above Average", sublabel: "Knowledge + Activity", color: "#57B98F"};
  if (speed === "low" && knowledge === "low")
    return {text: "Below Average", sublabel: "Low knowledge & speed", color: "#F57A47"};
  return {text: "Average Learner", sublabel: "Knowledge + Activity", color: "#F5A623"};
}

function levelColor(level: Level) {
  return level === "high" ? "#57B98F" : level === "average" ? "#F5A623" : "#F57A47";
}

function levelLabel(level: Level) {
  return level === "high" ? "High" : level === "average" ? "Average" : "Low";
}

const knowledgeLabelMap: Record<Level, string> = {high: "Excellent", average: "Average", low: "Low"};

const ProfileStatusBar: FC<Props> = ({knowledge, activity, speed, single}) => {
  const overview = getOverviewConfig(knowledge, activity, speed);

  const chips = [
    {
      label: overview.text,
      sublabel: overview.sublabel,
      color: overview.color,
      icon: <Users className="w-4 h-4" />,
    },
    {
      label: knowledgeLabelMap[knowledge],
      sublabel: "Knowledge",
      color: levelColor(knowledge),
      icon: <Star className="w-4 h-4" />,
    },
    {
      label: levelLabel(activity),
      sublabel: "Activity",
      color: levelColor(activity),
      icon: <Activity className="w-4 h-4" />,
    },
    {
      label: levelLabel(speed),
      sublabel: "Speed",
      color: levelColor(speed),
      icon: <Zap className="w-4 h-4" />,
    },
  ];

  const visibleChips = single ? chips.slice(0, 1) : chips;

  return (
    <div className="rounded-2xl overflow-hidden border border-brand-border bg-white">
      {/* color bar */}
      <div className="flex h-1.5">
        {chips.map((chip, i) => (
          <div key={i} className="flex-1" style={{backgroundColor: chip.color}} />
        ))}
      </div>
      {/* chip grid */}
      <div className={single ? "" : "grid grid-cols-2 divide-x divide-y divide-brand-border"}>
        {visibleChips.map((chip, i) => (
          <div key={i} className="flex items-center gap-2.5 px-3 py-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
              style={{backgroundColor: chip.color}}
            >
              {chip.icon}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <span className="font-bold text-xs text-brand-navy leading-tight">{chip.label}</span>
                <Tooltip position="right" text={chip.sublabel}>
                  <Info className="w-3 h-3 text-brand-muted flex-shrink-0 cursor-default" />
                </Tooltip>
              </div>
              <p className="text-xs text-brand-muted leading-tight">{chip.sublabel}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileStatusBar;
